import type {ISingleSelectFieldSettingsDefinition, ListObject} from "../../../../data/models/field/datatypes/SingleSelect";
import {HorizontalAlignment} from "../../../../utils/dom/DomUtils";
import {SelectInputV5, PillStyled} from "../../input/select/SelectInputV5";
import type {IInputPropsV5} from "../../../widgets/input/clicktoedit/InputUtils";
import Flag from "../../icons/flag.svg?react";
import Circle from "../../icons/circle.svg?react";
import type {DisplayFormat} from "../ListBuilderInputV5";

export const SingleSelectDropdownMaxHeight = 230;
export const SingleSelectDropdownFixedWidth = 272;

export const renderIconComponent = (type: DisplayFormat) => {
	switch (type) {
		case "flag":
			return <Flag />;
		case "pill":
			return <Circle />;
		case "colorBar":
		case "colorBarFill":
			return <div className="colorline"></div>;
		default:
			return null;
	}
};

export const SingleSelectInputV5 = (props: IInputPropsV5<ISingleSelectFieldSettingsDefinition>) => {
	const {value, onChange, disabled, inline} = props;
	let list = props.dataTypeSettings.choiceList;
	const type = props.dataTypeSettings?.type;

	let selectedOption = list.find((item) => item.value === value);
	const selectedIsRemoved = value && !selectedOption;

	if (selectedIsRemoved) {
		selectedOption = {value: value, color: ""};
		list = [...list, selectedOption];
	}

	let focus = false;

	const onFocus = (value: boolean) => {
		if (!value) {
			focus = false;
		}
	};

	const onClick = (e: React.MouseEvent) => {
		props.onClick?.(e);
	};

	const renderPill = (option: ListObject) => {
		return (
			<PillStyled
				$color={option.color ? `#${option.color}` : ""}
				$type={type}
			>
				{renderIconComponent(type)}
				{option.value}
			</PillStyled>
		);
	};

	const renderSelectedOption = (selected: ListObject) => {
		return (
			<PillStyled
				$color={selected.color ? `#${selected.color}` : ""}
				$type={type}
				$inline={inline}
				$selected={true}
			>
				{renderIconComponent(type)}
				<span>{selected.value}</span>
			</PillStyled>
		);
	};

	const fetchValueFromOption = (option: ListObject | null) => {
		if (option === null) {
			onChange?.(null);
		} else {
			onChange?.(option.value);
		}
	};

	const renderOptionValue = (option: ListObject | null) => {
		return option?.value ?? null;
	};

	return (
		<SelectInputV5
			options={list}
			selected={selectedOption || null}
			type={type}
			disabled={disabled}
			nullOption={true}
			nullLabel="-"
			placeholder="-"
			sort={false}
			focused={focus}
			onClick={onClick}
			onFocus={onFocus}
			horizontalAlignment={inline ? HorizontalAlignment.center : undefined}
			inline={inline}
			dropdownIcon={false}
			usePillStyle={true}
			onChange={fetchValueFromOption}
			isSameWidth={false}
			fullWidth={true}
			dropdownMaxHeight={`${SingleSelectDropdownMaxHeight}px`}
			dropdownFixedWidth={`${SingleSelectDropdownFixedWidth}px`}
			stringifyOption={renderOptionValue}
			renderOptionWhenClosed={renderSelectedOption}
			renderOptionWhenOpen={renderPill}
			closeIcon={true}
			selectedIsRemoved={selectedIsRemoved}
		/>
	);
};
